import React, { useEffect, useState } from "react";
import { Container, Image } from "react-bootstrap";
import { Link, useLocation } from "react-router-dom";

import { WEB_TITLE } from "../../constants/config_ui";
import { COMMON_ROUTE } from "../../constants/routes";
import { trackEventWithTimeout } from "../../service/event-service";
import logo from "../../assets/images/logo-with-text.png";

const databaseRedFlags = [
  {
    title: "An unexpected high-value resource",
    description:
      "Pause when a useful report, database, or invitation arrives without context. Confirm that you were expecting it.",
  },
  {
    title: "Sender and domain details",
    description:
      "Check the full sender address and exact website domain. Small spelling changes can be easy to miss.",
  },
  {
    title: "The link destination",
    description:
      "Before opening a link, preview its destination and ask whether it matches where the message says it will go.",
  },
  {
    title: "Pressure to act quickly",
    description:
      "Urgency, exclusivity, and fear of missing out can rush a decision. Take a moment and verify through a trusted channel.",
  },
];

const googleRedFlags = [
  {
    title: "An unexpected security alert",
    description:
      "An “app password created” or “new sign-in” alert you don’t recognise is a reason to pause. Open your account directly, not through the email.",
  },
  {
    title: "The display name is not the sender",
    description:
      "A message can show “Google” as the sender while coming from another address. Check the real email address, not just the name.",
  },
  {
    title: "The button did not go to Google",
    description:
      "Real Google alerts link to accounts.google.com or myaccount.google.com. Hover the button and confirm the destination before you sign in.",
  },
  {
    title: "Never sign in from an email link",
    description:
      "Type the address yourself or use a saved bookmark. A sign-in page reached from an email link is the most common way credentials are captured.",
  },
];

export default function Training() {
  const location = useLocation();
  const [lessonComplete, setLessonComplete] = useState(false);
  const [savingLesson, setSavingLesson] = useState(false);
  const [completionMessage, setCompletionMessage] = useState("");
  const params = new URLSearchParams(location.search);
  const isGoogleSignin = params.get("s") === "google-signin";
  const hasSigninForm = isGoogleSignin;
  const redFlags = isGoogleSignin ? googleRedFlags : databaseRedFlags;
  const trackingStatus =
    location.state && location.state.trackingStatus
      ? location.state.trackingStatus
      : null;

  useEffect(() => {
    window.document.title = `Security awareness exercise | ${WEB_TITLE}`;
  }, []);

  const completeLesson = async () => {
    if (lessonComplete || savingLesson) {
      return;
    }

    setSavingLesson(true);
    setCompletionMessage("");
    const result = await trackEventWithTimeout("lesson_completed");

    if (result.recorded) {
      setLessonComplete(true);
      setCompletionMessage("Thank you. You have completed the lesson.");
    } else if (result.reason === "timeout") {
      setCompletionMessage(
        "Lesson complete. Please keep this page open while confirmation finishes syncing."
      );
      setSavingLesson(false);
      result.pending.then((finalResult) => {
        if (finalResult.recorded) {
          setLessonComplete(true);
          setCompletionMessage("Thank you. You have completed the lesson.");
        } else {
          setCompletionMessage(
            "We could not save your completion. Please keep this page open and try again."
          );
        }
      });
      return;
    } else {
      setCompletionMessage(
        "We could not save your completion. Please keep this page open and try again."
      );
    }

    setSavingLesson(false);
  };

  return (
    <div className="training-page">
      <header className="training-header">
        <Container>
          <Link
            className="training-logo"
            to={{ pathname: COMMON_ROUTE.home.path, search: location.search }}
            aria-label={`${WEB_TITLE} home`}
          >
            <Image src={logo} alt={WEB_TITLE} />
          </Link>
          <span className="exercise-label">Authorized security exercise</span>
        </Container>
      </header>

      <main>
        <Container className="training-container">
          <section className="training-intro" aria-labelledby="training-title">
            <span className="training-eyebrow">TRAINING REVEAL</span>
            <h1 id="training-title">
              This was an authorized phishing awareness exercise.
            </h1>
            <p className="training-lead">
              {isGoogleSignin
                ? "You’re not in trouble. This exercise tested whether a look-alike security alert could lead to signing in on a page that only looked like Google."
                : "You’re not in trouble. This exercise is a chance to practise a quick pause before opening an unexpected link."}
            </p>
            <div className="privacy-note">
              <span aria-hidden="true">✓</span>
              <p>
                {hasSigninForm ? (
                  <>
                    <strong>
                      No credential value or email address was transmitted or
                      stored.
                    </strong>
                    {" "}
                    Anything typed into the simulation form was discarded
                    before this training page opened.
                  </>
                ) : (
                  <>
                    <strong>
                      No credentials or email address were requested.
                    </strong>
                    {" "}
                    Nothing sensitive was entered on this page.
                  </>
                )}
              </p>
            </div>
            {trackingStatus && trackingStatus !== "recorded" && (
              <div
                className={`tracking-notice ${trackingStatus}`}
                role="status"
              >
                {trackingStatus === "pending"
                  ? "Your simulation activity is still syncing. Please keep this page open."
                  : "We could not confirm the simulation click. Please keep this page open and tell the training facilitator."}
              </div>
            )}
          </section>

          <section className="red-flags" aria-labelledby="red-flags-title">
            <div className="section-heading">
              <span className="training-eyebrow">WHAT TO CHECK</span>
              <h2 id="red-flags-title">Four red flags to notice next time</h2>
              <p>
                Any one signal may be harmless. A few signals together are a
                good reason to stop and verify.
              </p>
            </div>

            <ol className="red-flag-grid">
              {redFlags.map((flag, index) => (
                <li key={flag.title}>
                  <span className="red-flag-number">
                    {String(index + 1).padStart(2, "0")}
                  </span>
                  <h3>{flag.title}</h3>
                  <p>{flag.description}</p>
                </li>
              ))}
            </ol>
          </section>

          <section className="training-finish" aria-labelledby="finish-title">
            <div>
              <span className="training-eyebrow">ONE-MINUTE TAKEAWAY</span>
              <h2 id="finish-title">Pause. Inspect. Verify.</h2>
              <p>
                If a message feels unusual, contact the sender through a channel
                you already trust before continuing.
              </p>
            </div>
            <button
              type="button"
              className="lesson-complete-button"
              onClick={completeLesson}
              disabled={lessonComplete || savingLesson}
            >
              {savingLesson
                ? "Saving completion…"
                : lessonComplete
                ? "Lesson completed"
                : "Complete this lesson"}
              <span aria-hidden="true">{lessonComplete ? "✓" : "→"}</span>
            </button>
            <p className="completion-status" role="status" aria-live="polite">
              {completionMessage}
            </p>
          </section>

          <Link
            className="back-to-article"
            to={{
              pathname: isGoogleSignin
                ? COMMON_ROUTE.accountSignin.path
                : COMMON_ROUTE.home.path,
              search: location.search,
            }}
          >
            ← Return to {isGoogleSignin ? "the sign-in page" : "the article"}
          </Link>
        </Container>
      </main>
    </div>
  );
}
